import { Close } from '@mui/icons-material';
import { Box, CircularProgress, Dialog, alpha } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { theme } from 'app/providers/ThemeProvider/config/theme';
import placeholder from 'shared/assets/images/catalog-item.svg';
import { transformFergusonImageUrl } from 'shared/utils/urlUtils/urlUtils';
import { Image } from 'widgets/Image';

interface ImageLightboxProps {
    open: boolean;
    onClose: () => void;
    imageUrl?: string | null;
}

const LOADER_DELAY_MS = 150;

export const ImageLightbox = memo(
    ({ open, onClose, imageUrl }: ImageLightboxProps) => {
        const transformedImageUrl = useMemo(
            () => transformFergusonImageUrl(imageUrl),
            [imageUrl],
        );

        const [imageLoadError, setImageLoadError] = useState(false);
        const [isImageLoading, setIsImageLoading] = useState(true);
        const [showLoader, setShowLoader] = useState(false);
        const loaderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const isLoadingRef = useRef(true);

        const shouldUseFallback = useMemo(
            () =>
                imageLoadError &&
                transformedImageUrl !== imageUrl &&
                transformedImageUrl != null,
            [imageLoadError, transformedImageUrl, imageUrl],
        );

        const imageSrc = shouldUseFallback ? imageUrl : transformedImageUrl;
        const isLoaderVisible = showLoader && isImageLoading;

        const clearLoaderTimeout = useCallback(() => {
            if (loaderTimeoutRef.current) {
                clearTimeout(loaderTimeoutRef.current);
                loaderTimeoutRef.current = null;
            }
        }, []);

        const handleImageComplete = useCallback(() => {
            isLoadingRef.current = false;
            setIsImageLoading(false);
            setShowLoader(false);
            clearLoaderTimeout();
        }, [clearLoaderTimeout]);

        // Reset error and loading state when dialog opens/closes or when imageUrl changes
        useEffect(() => {
            setImageLoadError(false);
            setIsImageLoading(true);
            setShowLoader(false);
            isLoadingRef.current = true;
            clearLoaderTimeout();

            // Only show loader after a delay to avoid flashing on fast loads
            if (open && imageSrc) {
                loaderTimeoutRef.current = setTimeout(() => {
                    if (isLoadingRef.current) {
                        setShowLoader(true);
                    }
                }, LOADER_DELAY_MS);
            }

            return clearLoaderTimeout;
        }, [open, imageUrl, transformedImageUrl, imageSrc, clearLoaderTimeout]);

        const handleImageError = useCallback(() => {
            // Trigger fallback if we're currently trying to load the transformed URL
            if (!shouldUseFallback && transformedImageUrl !== imageUrl) {
                setImageLoadError(true);
            }
            handleImageComplete();
        }, [
            shouldUseFallback,
            transformedImageUrl,
            imageUrl,
            handleImageComplete,
        ]);

        const handleImageLoad = handleImageComplete;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        width: 'auto',
                        height: 'auto',
                        margin: 0,
                    },
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: alpha(theme.palette.common.white, 0.8),
                    },
                }}
            >
                <Box
                    onClick={onClose}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        right: 16,
                        zIndex: 1400,
                        cursor: 'pointer',
                        color: theme.palette.common.black,
                        '&:hover': {
                            opacity: 0.8,
                        },
                    }}
                    aria-label="Close lightbox"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            onClose();
                        }
                    }}
                >
                    <Close sx={{ fontSize: 32 }} />
                </Box>
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '30px',
                            background: '#FFF',
                            borderRadius: '4px',
                            position: 'relative',
                            minWidth: '100px',
                            minHeight: '100px',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1,
                                opacity: isLoaderVisible ? 1 : 0,
                                transition: showLoader
                                    ? 'opacity 0.2s ease-in-out'
                                    : 'none',
                                pointerEvents: isLoaderVisible
                                    ? 'auto'
                                    : 'none',
                            }}
                        >
                            <CircularProgress
                                size={48}
                                sx={{ color: '#DCDCDC' }}
                            />
                        </Box>
                        <Image
                            src={open ? imageSrc : null}
                            onError={handleImageError}
                            onLoad={handleImageLoad}
                            imageStyle={{
                                width: 'auto',
                                height: 'auto',
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                height: '100%',
                                maxWidth: 'calc(90vw - 60px)',
                                maxHeight: 'calc(90vh - 60px)',
                            }}
                            placeholder={placeholder}
                        />
                    </Box>
                </Box>
            </Dialog>
        );
    },
);
